/**
 * @voltxampere/presets — Op-amp inverting amplifier (#11)
 *
 * V_in via Rin to in_neg, feedback Rf from out to in_neg, in_pos
 * to gnd. Closed-loop gain Av = -Rf/Rin. With Rin = 1 kΩ,
 * Rf = 10 kΩ, Av = -10 → V(out) = -1 V for V_in = 0.1 V (kept
 * within the default ±12 V saturation rail).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const opampInverting = {
    id: "opamp-inverting",
    name: { tr: "Op-Amp Tersleyici", en: "Op-Amp Inverting" },
    category: "amplifier",
    difficulty: 3,
    description: {
        tr: "Tersleyici op-amp yükselteç. Rin=1kΩ, Rf=10kΩ → Av = −10.",
        en: "Inverting op-amp amplifier. Rin=1kΩ, Rf=10kΩ → Av = −10.",
    },
    // Sprint 25 R2 lane 4D-A — hand-authored layout. V1 far left,
    // Rin horizontal between V1 and the op-amp's in_neg, Rf
    // horizontal above the op-amp providing feedback from `out` back
    // to in_neg. in_pos lands on net_gnd. Feedback drops vertically
    // at x=20 from Rf.p1 down to in_neg; in_pos goes around to GND1
    // along y=140 to avoid the gnd rail at x=-340.
    circuit: makeCircuit({
        slot: 11,
        name: "Op-Amp Inverting",
        description: "Closed-loop gain Av = −10",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "in" }),
            makeNet({ name: "fb" }),
            makeNet({ name: "out" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 0.1 },
                terminals: { pos: "net_in", neg: "net_gnd" },
                label: "V1",
                position: { x: -340, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "Rin",
                type: "resistor",
                params: { resistance: 1_000 },
                terminals: { p1: "net_in", p2: "net_fb" },
                label: "Rin",
                position: { x: -180, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "Rf",
                type: "resistor",
                params: { resistance: 10_000 },
                terminals: { p1: "net_fb", p2: "net_out" },
                label: "Rf",
                position: { x: 40, y: -100 },
                rotation: 0,
            }),
            makeComponent({
                id: "U1",
                type: "opamp",
                params: {},
                terminals: { in_pos: "net_gnd", in_neg: "net_fb", out: "net_out" },
                label: "U1",
                position: { x: 40, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -340, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            // net_in: V1.pos (-340,-10) → Rin.p1 (-200,0). Routes above
            // V1 to clear the gnd rail.
            {
                id: "w_in",
                netId: "net_in",
                path: [
                    { x: -340, y: -10 },
                    { x: -340, y: -40 },
                    { x: -200, y: -40 },
                    { x: -200, y: 0 },
                ],
                style: "orthogonal",
            },
            // net_fb leg A: Rin.p2 (-160,0) → in_neg (20,-4) via y=-4.
            {
                id: "w_fb_in",
                netId: "net_fb",
                path: [
                    { x: -160, y: 0 },
                    { x: -160, y: -4 },
                    { x: 20, y: -4 },
                ],
                style: "orthogonal",
            },
            // net_fb leg B: in_neg (20,-4) → Rf.p1 (20,-100). Vertical
            // feedback drop at x=20.
            {
                id: "w_fb_rf",
                netId: "net_fb",
                path: [
                    { x: 20, y: -4 },
                    { x: 20, y: -100 },
                ],
                style: "orthogonal",
            },
            // net_out: U1.out (60,0) → Rf.p2 (60,-100). Vertical at x=60.
            {
                id: "w_out",
                netId: "net_out",
                path: [
                    { x: 60, y: 0 },
                    { x: 60, y: -100 },
                ],
                style: "orthogonal",
            },
            // net_gnd leg A: V1.neg (-340,10) → GND1.p (-340,71).
            {
                id: "w_gnd_v1",
                netId: "net_gnd",
                path: [
                    { x: -340, y: 10 },
                    { x: -340, y: 71 },
                ],
                style: "orthogonal",
            },
            // net_gnd leg B: in_pos (20,4) → GND1.p (-340,71). Routes
            // far below the components on y=140 to avoid the V1↔GND rail.
            {
                id: "w_gnd_inpos",
                netId: "net_gnd",
                path: [
                    { x: 20, y: 4 },
                    { x: 20, y: 140 },
                    { x: -340, y: 140 },
                    { x: -340, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "out",
            analysis: "dc",
            value: -1,
            tolerance: 0.05,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Tersleyici kazanç Av = −Rf/Rin: çıkış faz tersine döner.",
            en: "Inverting gain Av = −Rf/Rin: output is phase-inverted.",
        },
        {
            tr: "Virtual short ve virtual ground: in_neg sürücüsüz halde 0 V'a kilitlenir.",
            en: "Virtual short and virtual ground: in_neg locks to 0 V without driver.",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 2"],
};
//# sourceMappingURL=opamp-inverting.js.map